import { ChangeEvent, FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
// style
import { FilterContainer, SelectCompWithMargin } from "./Filters.styled";
// components
import { Spin } from "@findemdev/maui";
// types
import { IFilterObj, IFilterProps, IOption } from "./types";
import { FilterObj, FilterType } from "./constants";
import { debounce } from "lodash";
import { useLazyGetCredBuilderFiltersACQuery } from "../../../../../services/matches";
import UserContext from "../../../../../contexts/UserContext";
// util
import _debounce from "lodash/debounce";
import useGetCredentialJoid from "../../../hooks/useGetCredentialJoid";
import { useFetchAllUsersQuery } from "../../../../../services/payments";
import { decodeString } from "@findemdev/findem-helpers";
import { TabId } from "../NavigationTabs";

const Filters: FC<IFilterProps> = ({
  onFilterChange,
  filterObj,
  activeTab,
}) => {
  const [filterValues, setFilterValues] = useState<IFilterObj>({});

  const [options, setOptions] = useState<IOption[]>([]);

  // context user
  const userModel = useContext(UserContext);
  const credentialJoid = useGetCredentialJoid({});

  // api
  const [
    filtersAC,
    { isFetching: isAutoCompletePending, isLoading: isAutoCompleteLoading },
  ] = useLazyGetCredBuilderFiltersACQuery();
  const { data: allUsers, isFetching: isFetchingAllUsers } = useFetchAllUsersQuery({ joid: userModel.getUser().uid });

  const userOptions = useMemo(() => {
    return allUsers?.users.map((user) => ({
      value: user._id,
      label: decodeString(user.local.name),
    })) ?? [];
  }, [allUsers]);

  const selectFilters: string[] = useMemo(() => {
    const isLibOrMarqueeTab = activeTab === TabId["cred-library"] || activeTab === TabId["marquees"];
    const isFavoritesTab = activeTab === TabId["favorites"];
    const isSharedTab = isFavoritesTab || isLibOrMarqueeTab;

    return Object.keys(FilterObj).filter((key) => {
      if (key === FilterType["OWNER"]) {
        return isSharedTab; // show owner filter only in tabs where shared credentials are shown
      }
      if (key === FilterType["FAVORITE"] && isFavoritesTab) { // hide favorites filter in favorites tab cause the tab shows favorites already
        return false;
      }
      return true;
    });
  }, [activeTab]);

  const favoritesOptions = useMemo(() => ([{
    label: "Favorites",
    value: credentialJoid,
  }]), [credentialJoid]);

  const effectiveOptions = useCallback(({ filter }: { filter: string}) => {
    if (filter === FilterType['OWNER']) {
      return userOptions;
    } else if (filter === FilterType['FAVORITE']) {
      return favoritesOptions;
    } else {
      return options;
    }
  }, [favoritesOptions, userOptions, options]);

  const _handleDateRangeChange = (_dateRange: [string, string]) => {
    // onFilterChange({ ...filterObj, date_range: dateRange });
  };
  const handleFiltersChange = useCallback(
    (key: string, value: string) => {
      if (value === "-1") {
        return;
      }
      // filter out current key
      onFilterChange({
        ...filterObj,
        [key]: {
          field: FilterObj[key].filterKeyword ?? key,
          operator: FilterObj[key].operator,
          value: value,
        },
      });
    },
    [filterObj, onFilterChange]
  );

  const debouncedHandleFiltersChange = useCallback(_debounce(handleFiltersChange, 1000),
    [handleFiltersChange]);

  const _onNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      debouncedHandleFiltersChange(FilterType['NAME'] ,e.target.value);
    },
    [handleFiltersChange]
  );

  const handleSearch = useCallback(
    debounce(async (input: string, filter: string) => {
      if (filter === FilterType['OWNER']) {
        return;
      }
      if (input) {
        setOptions([
          {
            label: "Loading...",
            value: "-1",
          },
        ]);
        const response = await filtersAC({
          prefix: input,
          field: FilterObj[filter].autoCompleteKeyword ?? filter,
          joid: credentialJoid,
        });
          const result = response.data;
          if (result?.data) {
            setOptions(
              result.data.map((item) => ({
                label: item.value,
                value: item.value,
              }))
            );
        }
      } else {
        setOptions([]);
      }
    }, 500),
    [credentialJoid]
  );

  useEffect(() => {
    setFilterValues(filterObj);
  }, [filterObj]);

  return (
    <FilterContainer>
      {/* Filters of type DD */}
      {selectFilters.map((filter: string) => (
        <SelectCompWithMargin
          loading={filter === FilterType['OWNER'] && isFetchingAllUsers}
          showSearch={filter !== FilterType['FAVORITE']}
          allowClear={true}
          dropdownMatchSelectWidth={false}
          onSearch={(input: string) => handleSearch(input, filter)}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          key={filter}
          placeholder={<div>{FilterObj[filter].label}</div>}
          value={
            filterValues && filterValues[filter] && filterValues[filter]?.value
              ? filterValues[filter]
              : undefined
          }
          onChange={(value: any) => handleFiltersChange(filter, value)}
          options={effectiveOptions({ filter })}
          notFoundContent={
            isAutoCompletePending || isAutoCompleteLoading ? (
              <Spin size="small" />
            ) : null
          }
          onBlur={() => setOptions([])}
        />
      ))}
    </FilterContainer>
  );
};

export default Filters;
